import { ArticleFormatsExtension, FormatExtension, jArticleList } from '../../types';
import {
    AdminLicensesAssigned,
    AdminLicensesNotAssigned,
    AdminLicenseStats,
    ObjectWithoutLicenseFilter,
} from '../../types/adminLicense';
import {
    GetLicenseArticle,
    GetLicenseArticlesParams,
    GetLicenseKindUsersParams,
    UserLicense,
} from '../../types/license';
import { jUserLittle } from '../../types/user';
import { FormatList, PagingResults } from '../../types/utils';
import { combineFormat } from '../../util';
import { OF } from '../../util/network';
import { JBaseApi } from '../base';

export class AdminLicenseApi extends JBaseApi {
    /**
     * Retrieve the user licenses stats
     */
    public getLicenseUserStatistics(signal?: AbortSignal) {
        return this.network.post<AdminLicenseStats>(
            {
                o: 'licenseManager',
                f: 'userStatistics',
            } as OF,
            signal,
        );
    }

    /**
     * Retrieve the licenses stats for a given kind
     */
    public getLicenseStatistics(kind: string, type?: string, signal?: AbortSignal) {
        return this.network.post<AdminLicenseStats>(
            {
                o: 'licenseManager',
                f: 'statistics',
                kind,
                type,
            } as OF,
            signal,
        );
    }

    /**
     * Assigns a license to the content with the provided uri (if any is available)
     * @param uri URI of the object to which a license must be assigned
     * @returns boolean
     */
    public assignLicense(uri: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'assignLicense',
            uri,
        } as OF);
    }

    /**
     * Assigns a license to a given kind with the provided uri (if any is available)
     * @param uri URI of the object to which a license must be assigned
     * @param kind the kind of license which must be assigned
     * @returns boolean
     */
    public assignKindLicense(uri: string, kind: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'assignLicenseKind',
            uri,
            kind,
        } as OF);
    }

    /**
     * Unassigns the license linked to the content with the provided uri
     * @param uri URI of the object to which a license must be unassigned
     * @returns boolean
     */
    public unAssignLicense(uri: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'unassignLicense',
            uri,
        } as OF);
    }

    /**
     * Unassigns the license linked to the given kind with the provided uri
     * @param uri URI of the object to which a license must be unassigned
     * @param kind the kind of license which must be unassigned
     * @returns boolean
     */
    public unAssignKindLicense(uri: string, kind: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'unassignLicenseKind',
            uri,
            kind,
        } as OF);
    }

    /**
     * Retrieve the assignment license user mode between automatic | manual
     * @param type retrieve the automatic license assignation
     * @param signal the signal to cancel the request
     * @returns a string who's the current assignment selected
     */
    public getAssignmentMode(type: string, signal?: AbortSignal) {
        return this.network.post<string>(
            {
                o: 'licenseManager',
                f: 'getAssignmentMode',
                type,
            } as OF,
            signal,
        );
    }

    /**
     * Change the mode which the license are affected to the users
     * @param type the type of license that would automatically be assigned
     * @param assignmentMode the mode which the license are affected (automatic | manual)
     * @returns boolean
     */
    public setAssignmentMode(type: string, assignmentMode: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'setAssignmentMode',
            value: assignmentMode,
            type,
        } as OF);
    }

    /**
     * Assign the available licenses
     * @param type the type of licenses for which we will assign the remaining available one
     * @param kind particular license kind to assign
     * @returns boolean
     */
    public assignPossiblesLicenses(type: string, kind?: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'assignPossiblesLicenses',
            type,
            kind,
        } as OF);
    }

    /**
     * Unassign licenses for inactive users
     * @param type the type of licenses for which we will unassign upon inactive user
     * @param kind particular license kind to unassign
     * @returns boolean
     */
    public unAssignUnusedLicenses(type: string, kind?: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'unassignUnusedLicenses',
            type,
            kind,
        } as OF);
    }

    /**
     * Assign available licenses and unassign license for inactive users
     * @param type the type of licenses for which we will unassign and assign the possible licenses
     * @param kind particular license kind to unassign and assign
     * @returns boolean
     */
    public optimizeLicenses(type: string, kind?: string) {
        return this.network.postCSRF<boolean>({
            o: 'licenseManager',
            f: 'optimizeLicenses',
            type,
            kind,
        } as OF);
    }

    /**
     * Retrieves the number of licenses of a given type available. License type is
     * different from the content type to which it can be assigned to.
     *
     * @param type The type of license for which we want to know the availability
     * @param filter the filters array the request can be called with
     * @returns A promise with the number of object without license
     */
    public getObjectsWithoutLicense(type: string, filter?: ObjectWithoutLicenseFilter[]) {
        return this.network.post<number>({
            o: 'licenseManager',
            f: 'countObjectsWithoutLicense',
            type,
            filter,
        } as OF);
    }

    /**
     * Retireve the list of licenses assigned to content of a given type
     *
     * @param type type of content for which we want to retrieve the licenses not assigned
     * @returns The number of licenses depending on the type used as param
     */
    public getLicensesNotAssigned(type: string) {
        return this.network.post<AdminLicensesNotAssigned>({
            o: 'licenseManager',
            f: 'countLicensesNotAssigned',
            type,
        } as OF);
    }

    /**
     * Retireve the list of licenses assigned to content of a given type
     *
     * @param type type of content for which we want to retrieve the licenses
     * @returns The list of licenses assigned to content of the provided type
     */
    public getLicensesAssigned(type: string) {
        return this.network.post<AdminLicensesAssigned[]>({
            o: 'licenseManager',
            f: 'getLicensesAssigned',
            type,
        } as OF);
    }

    public getLicenseUsersByKind<T extends jUserLittle = jUserLittle>(
        { ...params }: GetLicenseKindUsersParams,
        format: FormatList,
        signal?: AbortSignal,
    ) {
        return this.network.post<PagingResults<UserLicense<T>>>(
            {
                o: 'licenseManager',
                f: 'getUsers',
                ...params,
                format,
            } as OF,
            signal,
        );
    }

    public getLicenseArticles<
        T extends jArticleList = jArticleList,
        K extends ArticleFormatsExtension = ArticleFormatsExtension,
    >(
        payload: GetLicenseArticlesParams,
        params: { signal?: AbortSignal; formatExtension: FormatExtension; format: FormatList },
    ) {
        return this.network.post<PagingResults<GetLicenseArticle<T, K>>>(
            {
                o: 'licenseManager',
                f: 'getArticles',
                ...payload,
                format: combineFormat(params.format, params.formatExtension),
            } as OF,
            params.signal,
        );
    }
}
